import {Sponsored} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_sponsored.types';
import {HeroBrandsSection_BrandElement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useInViewEffect} from '@aboutyou/ui-components';
import {startTransition, useCallback, useRef, useState} from 'react';
import {usePreloadBrandContent} from './usePreloadBrandContent';

export const SLIDER_TIME_S = 5;

type HeroBrandsSliderElement = HeroBrandsSection_BrandElement & {
  sponsored?: Sponsored;
};

export const useHeroBrandsSlider = (elements: HeroBrandsSliderElement[]) => {
  // we might have different component on the same stream page, having same stream position
  // so we need to have a unique key for each component
  const uniqueKey = `heroBrandsSlider_${elements
    .map(element => element.brand.id)
    .join('')}`;

  const preloadBrandContent = usePreloadBrandContent(elements);

  const sliderTimerRef = useRef<NodeJS.Timeout>(undefined);
  const preloadTimerRef = useRef<NodeJS.Timeout>(undefined);

  const [userInteracted, setUserInteracted] = useState(false);
  const [paused, setPaused] = useState(userInteracted);
  const [activeBrandIndex, setActiveBrandIndex] = useHistoryPageState(0, {
    uniqueKey,
  });

  const {ref} = useInViewEffect<HTMLDivElement>(
    inView => {
      clearTimeout(sliderTimerRef.current);

      if (!inView) {
        pause();
        setUserInteracted(false);
        return;
      }

      if (!userInteracted) {
        start();
      }
    },
    {rootMargin: '0px -40px', threshold: 0.4},
  );

  const changeSlide = (index: number) => {
    startTransition(() => {
      setActiveBrandIndex(index);
    });
  };

  const start = () => {
    const nextBrandIndex =
      activeBrandIndex + 1 === elements.length ? 0 : activeBrandIndex + 1;

    sliderTimerRef.current = setTimeout(() => {
      if (userInteracted) {
        return;
      }

      changeSlide(nextBrandIndex);
    }, SLIDER_TIME_S * 1000);

    preloadTimerRef.current = setTimeout(() => {
      preloadBrandContent(nextBrandIndex);
    }, (SLIDER_TIME_S / 2) * 1000);

    startTransition(() => {
      setPaused(false);
    });
  };

  const pause = useCallback(() => {
    clearTimeout(sliderTimerRef.current);
    clearTimeout(preloadTimerRef.current);
    sliderTimerRef.current = undefined;
    preloadTimerRef.current = undefined;
    startTransition(() => {
      setPaused(true);
    });
  }, []);

  const pauseTimer = () => {
    pause();
    setUserInteracted(true);
  };

  return {
    activeBrandIndex,
    activeBrand: elements[activeBrandIndex]!,
    changeSlide,
    paused,
    pauseTimer,
    preloadBrandContent,
    ref,
    uniqueKey,
  };
};
